import React, { useRef, useState } from "react";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, FileText, Image as ImgIcon, Film, FileSpreadsheet, Loader2 } from "lucide-react";

const ICON_BY_EXT = {
  pdf: FileText, docx: FileText, txt: FileText, md: FileText,
  xlsx: FileSpreadsheet, csv: FileSpreadsheet,
  pptx: FileText,
  png: ImgIcon, jpg: ImgIcon, jpeg: ImgIcon, gif: ImgIcon, webp: ImgIcon,
  mp4: Film, mov: Film,
  py: FileText, ipynb: FileText, zip: FileText,
};

function extOf(name) {
  return (name || "").split(".").pop().toLowerCase();
}

export function FileTile({ file, onRemove, dark = true }) {
  const ext = extOf(file.filename || file.original_filename || "");
  const Icon = ICON_BY_EXT[ext] || FileText;
  const url = file.url || (file.storage_path ? `/api/files/${file.storage_path}` : null);
  const href = url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : null;
  const kb = Math.max(1, Math.round((file.size || 0) / 1024));
  return (
    <div
      data-testid="file-tile"
      className={`flex items-center gap-3 rounded-lg border ${dark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"} px-3 py-2`}
    >
      <Icon className={`w-4 h-4 ${dark ? "text-[#F26A21]" : "text-[#B84A0C]"}`} />
      <div className="min-w-0 flex-1">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer"
             className={`block text-sm truncate ${dark ? "text-gray-100 hover:text-white" : "text-gray-800 hover:text-black"}`}>
            {file.filename || file.original_filename}
          </a>
        ) : (
          <p className={`text-sm truncate ${dark ? "text-gray-100" : "text-gray-800"}`}>
            {file.filename || file.original_filename}
          </p>
        )}
        <p className={`text-[11px] ${dark ? "text-gray-500" : "text-gray-400"}`}>
          {kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`}
        </p>
      </div>
      {onRemove && (
        <button onClick={() => onRemove(file)} data-testid="file-tile-remove"
                className={`p-1 rounded ${dark ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-500 hover:bg-gray-100"}`}>
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Uploads files via POST /api/files/upload.
 * Calls onUploaded(fileObj) for each successful upload.
 */
export default function FileUploader({
  kind = "generic",
  resourceType,
  resourceId,
  multiple = true,
  accept = ".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.zip,.csv,.txt,.md,.py,.ipynb",
  onUploaded,
  disabled = false,
  buttonLabel = "Subir archivo",
  hint = "PDF, Office, imágenes, videos, código. Máx 50 MB (200 MB para video · 500 MB para lección).",
  compact = false,
  dark = true,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (list) => {
    if (!list || !list.length) return;
    setUploading(true);
    let done = 0;
    for (const f of Array.from(list)) {
      try {
        const form = new FormData();
        form.append("file", f);
        const params = new URLSearchParams({ kind });
        if (resourceType) params.set("resource_type", resourceType);
        if (resourceId) params.set("resource_id", resourceId);
        const res = await api.post(`/files/upload?${params.toString()}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        onUploaded && onUploaded(res.data);
        done += 1;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        const msg = typeof detail === "object" ? detail?.message : formatApiError(detail);
        toast.error(`No se pudo subir "${f.name}": ${msg || "error"}`, {
          description: code ? `Código: ${code}` : undefined,
        });
      } finally {
        setProgress(0);
      }
    }
    setUploading(false);
    if (done && !compact) toast.success(`${done} archivo(s) subidos.`);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled || uploading}
        className="hidden"
        data-testid="file-uploader-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        data-testid="file-uploader-btn"
        className={`inline-flex items-center gap-2 rounded-md ${compact ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm"} font-medium transition-colors ${
          dark
            ? "border border-white/20 bg-white/5 text-white hover:bg-white/10 disabled:opacity-50"
            : "border border-[#B84A0C]/30 bg-[#B84A0C]/5 text-[#B84A0C] hover:brightness-110/10 disabled:opacity-50"
        }`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? `Subiendo… ${progress}%` : buttonLabel}
      </button>
      {!compact && hint && (
        <p className={`mt-1 text-[11px] ${dark ? "text-gray-500" : "text-gray-400"}`}>{hint}</p>
      )}
    </div>
  );
}
